/**
 * 每日登录弹窗：首次 / 昨日已打卡 / 昨日未打卡 三种文案，童趣风格
 * 连续打卡时展示打卡页日历表盘；若昨日未打卡且有余量则显示「去补签」按钮
 */
import React, { useMemo } from 'react';
import WkModal from 'components/Modal';
import silmeImg from 'asset/img/silme.png';
import CheckinCalendar from '../../pages/Checkin/CheckinCalendar';
import './index.scss';

/** 每日一句励志文案（按顺序轮换，每天首次进入时切换，仅存一句） */
const DAILY_MOTIVATION_TIPS = [
  '坚持这件事，本身就超酷的',
  '每天进步一点点，就很了不起',
  '语言的世界，藏在日复一日的坚持里',
  '你今天的点滴积累，都会变成未来的光',
  '勇敢开口，你就已经赢了胆怯',
  '慢慢来，你的每一次学习都算数',
  '多学一个词，世界就多开一扇窗',
  '你只管认真向前，时间自有答案',
  '每天的小努力，都在攒着大惊喜',
  '今天的你，又比昨天更厉害了一点',
  '别怕起步晚，就怕你不出发',
  '每一次打卡，都是给成长蓄力',
  '小小的坚持，会开出大大的花',
  '学好英语，去拥抱更广阔的世界',
  '你认真学习的样子，真的超耀眼',
  '日复一日的沉淀，终会闪闪发光',
  '今天的开口，是流利表达的起点',
  '没有白走的路，每一步都算数',
  '点滴积累，终会汇成江海',
  '坚持下去，你会遇见更厉害的自己',
];

const MOTIVATION_DATE_KEY = 'daily_motivation_tip_date';
const MOTIVATION_INDEX_KEY = 'daily_motivation_tip_index';

function localDateKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** 每个自然日首次调用时按顺序切换到下一句，同日多次调用返回同一句 */
function getDailyMotivationTip() {
  const today = localDateKey();
  const len = DAILY_MOTIVATION_TIPS.length;
  try {
    const savedDate = localStorage.getItem(MOTIVATION_DATE_KEY);
    let idx = parseInt(localStorage.getItem(MOTIVATION_INDEX_KEY) || '0', 10);
    if (Number.isNaN(idx)) idx = 0;
    idx = ((idx % len) + len) % len;

    if (savedDate === today) {
      return DAILY_MOTIVATION_TIPS[idx];
    }

    const nextIdx = savedDate == null ? 0 : (idx + 1) % len;
    localStorage.setItem(MOTIVATION_DATE_KEY, today);
    localStorage.setItem(MOTIVATION_INDEX_KEY, String(nextIdx));
    return DAILY_MOTIVATION_TIPS[nextIdx];
  } catch {
    let h = 0;
    for (let i = 0; i < today.length; i += 1) h = (h * 31 + today.charCodeAt(i)) >>> 0;
    return DAILY_MOTIVATION_TIPS[h % len];
  }
}

const FIRST_DAY_MSG = '欢迎开启闯关之旅！这是你的第 1 天，勇敢出发吧～';

const STREAK_MSGS = [
  (x) => `太棒啦！你已经连续打卡 ${x} 天，继续坚持，你超厉害的！`,
  (x) => `连续打卡 ${x} 天达成！好习惯正在养成，继续加油闯关～`,
  (x) => `连续坚持 ${x} 天，你就是最棒的小勇士！`,
  (x) => `又见面啦！连续打卡 ${x} 天，离目标又近一步啦✨`,
  (x) => `坚持就是胜利！你已经连续打卡 ${x} 天，今天也要元气满满！`,
  (x) => `叮～加油哦！连续 ${x} 天，奖励你一颗小星星🌟`,
];

const ENCOURAGE_MSGS = [
  '昨天忘记打卡没关系，今天重新开始，你依然很棒！',
  '偶尔断签很正常～今天继续打卡，好习惯马上回来！',
  '欢迎回来！从今天开始坚持，新的连续记录由你开启💪',
  '没关系，我们从头再来！今天打卡，继续向前闯关吧～',
  '小失误不影响优秀！今天记得打卡，重新点亮连续天数✨',
  '昨天暂时缺席，今天准时回归就很棒！开始今天的闯关吧～',
];

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// 把文案里的连续天数数字拆出来，用于强调展示
function renderContentWithStreak(text, streak) {
  if (streak == null || streak === '' || text == null) return text;
  const s = String(streak);
  const i = text.indexOf(s);
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <span className="daily-login-streak-num">{streak}</span>
      {text.slice(i + s.length)}
    </>
  );
}

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;

const DailyLoginModal = ({
  visible,
  onClose,
  variant,
  streak = 0,
  showMakeupButton = false,
  monthSigned = [],
  onMakeupClick,
}) => {
  const { title, content, emphasizeStreak } = useMemo(() => {
    if (variant === 'first') {
      return { title: '✨ 欢迎来到悟空听说', content: FIRST_DAY_MSG, emphasizeStreak: false };
    }
    if (variant === 'streak') {
      const fn = pickRandom(STREAK_MSGS);
      const text = fn(streak);
      return { title: '📅 每日一句', content: text, emphasizeStreak: true };
    }
    if (variant === 'encourage') {
      const text = pickRandom(ENCOURAGE_MSGS);
      return { title: '💪 加油呀', content: text, emphasizeStreak: false };
    }
    return { title: '', content: '', emphasizeStreak: false };
  }, [visible, variant, streak]);

  const dailySmartTip = useMemo(() => getDailyMotivationTip(), [visible]);

  const handleMakeup = () => {
    onClose?.();
    onMakeupClick?.();
  };

  return (
    <WkModal
      visible={visible}
      onCancel={onClose}
      onOk={onClose}
      okText="知道啦"
      cancelText="去补签"
      cancelVisble={showMakeupButton}
      onBtnVisable={showMakeupButton}
      onBtn={handleMakeup}
      maskClosable={false}
      cls="daily-login-modal"
      tongquStyle
      title={title}
      content={
        <div className="daily-login-content">
          {variant !== 'streak' && (
            <img src={silmeImg} alt="" className="daily-login-silme" />
          )}
          <p className="daily-login-smart-tip">{dailySmartTip}</p>
          <p className="daily-login-text">
            {emphasizeStreak ? renderContentWithStreak(content, streak) : content}
          </p>
          {variant === 'streak' && (
            <div className="daily-login-calendar-wrap" aria-hidden="true">
              <CheckinCalendar
                monthSigned={monthSigned}
                year={currentYear}
                month={currentMonth}
              />
            </div>
          )}
        </div>
      }
    />
  );
};

export default React.memo(DailyLoginModal);
